import { useQuery } from '@tanstack/react-query';
import API, { getMediaURL } from '../../../../context/API';
import { useActiveStoreId } from '../../../Stores/hooks';
import { requireActiveStoreId } from '../../../../utils/activeStore';
import { Category, CategoriesApiResponse, CategoryItem, RawCategory, RawCategoryItem } from '../types';
import { QUERY_KEYS } from '../constants';

function sortBySequence<T extends { sequence_number?: number | string }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : Number(a.sequence_number) || 0;
    const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : Number(b.sequence_number) || 0;
    return aSeq - bSeq;
  });
}

async function fetchCategories(search: string): Promise<Category[]> {
  const storeId = requireActiveStoreId();
  const params = new URLSearchParams({ storeId });
  if (search) params.append('search', search);

  const response = await API.get<CategoriesApiResponse>(`/categories?${params}`);

  if (!response.data?.data || !Array.isArray(response.data.data)) {
    throw new Error('Invalid response structure from API');
  }

  const categoriesData: Category[] = response.data.data
    .filter((category: RawCategory) => category && typeof category === 'object')
    .map((category: RawCategory) => {
      const itemsArray = Array.isArray(category.items) ? category.items : [];
      const itemsData: RawCategoryItem[] = itemsArray
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          ...item,
          picture: item.picture ? getMediaURL(item.picture) : '',
        }));

      const sortedItems = sortBySequence(itemsData);

      return {
        ...category,
        picture: category.picture && typeof category.picture === 'string' ? category.picture : '',
        items: sortedItems
          .filter((item) => item && item.id != null)
          .map((item) => item.id.toString()),
      };
    });

  return sortBySequence(categoriesData);
}

export function useCategoriesQuery(search = '', enabled = true) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.CATEGORIES, search, storeId],
    queryFn: () => fetchCategories(search),
    placeholderData: (prevData, prevQuery) =>
      prevQuery?.queryKey[2] === storeId ? prevData : undefined,
    enabled: enabled && !!storeId,
  });
}

export function useAllItemsQuery(enabled = true) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.ALL_ITEMS, storeId],
    queryFn: async (): Promise<CategoryItem[]> => {
      const response = await API.get<{ success: boolean; data: CategoryItem[] }>(
        `/items?storeId=${requireActiveStoreId()}`
      );
      if (!response.data?.data || !Array.isArray(response.data.data)) return [];
      return response.data.data.filter((item) => item && typeof item === 'object' && item.id != null);
    },
    enabled: enabled && !!storeId,
    staleTime: 5 * 60 * 1000,
  });
}
